/* *
 * ============================================================================
 * INTELLIGENT DELIVERY SPEED CALCULATOR
 * ============================================================================
 * Calculates realistic delivery times based on multiple factors:
 * - Word count / Pages
 * - Academic level (Undergraduate, Masters, PhD)
 * - Research type (Secondary vs Primary)
 * - Technical complexity
 * - External requirements (software, lab, field work)
 * - Service type and scope
 * 
 * Standard delivery is calculated first, then rush tiers adjust accordingly.
 * ============================================================================ */

'use strict';

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================
  
  // Base days per 1000 words by academic level
  const BASE_DAYS_PER_1000_WORDS = {
    high_school: 1.5,      // High school level - simpler requirements
    undergraduate: 2,       // Undergraduate - standard academic rigor
    masters: 3,             // Masters - higher research depth
    phd: 4.5,               // PhD - extensive research and analysis
    professional: 2.5       // Professional/Business writing
  };
  
  // Minimum days by academic level (even for small word counts)
  const MINIMUM_DAYS = {
    high_school: 2,
    undergraduate: 3,
    masters: 5,
    phd: 7,
    professional: 3
  };
  
  // Maximum days cap by academic level
  const MAXIMUM_DAYS = {
    high_school: 14,
    undergraduate: 21,
    masters: 35,
    phd: 60,
    professional: 21
  };
  
  // Research type multipliers
  const RESEARCH_TYPE_MULTIPLIERS = {
    secondary: 1.0,         // Desk research, literature review
    mixed: 1.35,            // Combination of primary and secondary
    primary_survey: 1.5,    // Surveys, questionnaires
    primary_interview: 1.7, // Interviews, focus groups
    primary_lab: 2.0,       // Laboratory experiments
    primary_field: 2.2,     // Field research, on-site data collection
    primary_software: 1.6   // Software-based analysis (SPSS, coding, simulations)
  };
  
  // Complexity multipliers based on technical level
  const COMPLEXITY_MULTIPLIERS = {
    basic: 0.9,             // Standard essay, simple analysis
    moderate: 1.0,          // Research paper, case study
    advanced: 1.25,         // Complex analysis, specialized topics
    expert: 1.5,            // Highly technical, specialized expertise required
    specialized: 1.75       // Rare expertise, multi-disciplinary
  };
  
  // Service type base adjustments
  const SERVICE_ADJUSTMENTS = {
    // ========================================
    // ACADEMIC SERVICES
    // ========================================
    essay: { baseMultiplier: 0.85, minDays: 1, label: 'Essay', category: 'academic' },
    research_paper: { baseMultiplier: 1.0, minDays: 3, label: 'Research Paper', category: 'academic' },
    case_study: { baseMultiplier: 1.1, minDays: 3, label: 'Case Study', category: 'academic' },
    literature_review: { baseMultiplier: 1.15, minDays: 4, label: 'Literature Review', category: 'academic' },
    dissertation_chapter: { baseMultiplier: 1.3, minDays: 5, label: 'Dissertation Chapter', category: 'academic' },
    full_dissertation: { baseMultiplier: 1.5, minDays: 14, label: 'Full Dissertation', category: 'academic' },
    thesis: { baseMultiplier: 1.4, minDays: 10, label: 'Thesis', category: 'academic' },
    proposal: { baseMultiplier: 1.2, minDays: 4, label: 'Research Proposal', category: 'academic' },
    report: { baseMultiplier: 1.0, minDays: 2, label: 'Report', category: 'academic' },
    presentation: { baseMultiplier: 0.7, minDays: 1, label: 'Presentation', category: 'academic' },
    annotated_bibliography: { baseMultiplier: 0.9, minDays: 2, label: 'Annotated Bibliography', category: 'academic' },
    reflection: { baseMultiplier: 0.75, minDays: 1, label: 'Reflection Paper', category: 'academic' },
    
    // ========================================
    // CAREER SERVICES - Writing (Quick turnaround)
    // ========================================
    resume: { baseMultiplier: 0.4, minDays: 2, maxDays: 5, label: 'Resume/CV', category: 'career', usesWordCount: false, baseDays: 3 },
    cover_letter: { baseMultiplier: 0.3, minDays: 1, maxDays: 3, label: 'Cover Letter', category: 'career', usesWordCount: false, baseDays: 2 },
    linkedin: { baseMultiplier: 0.35, minDays: 2, maxDays: 4, label: 'LinkedIn Profile', category: 'career', usesWordCount: false, baseDays: 3 },
    personal_statement: { baseMultiplier: 0.6, minDays: 3, maxDays: 7, label: 'Personal Statement', category: 'career', usesWordCount: true },
    lor: { baseMultiplier: 0.4, minDays: 2, maxDays: 5, label: 'Letter of Recommendation', category: 'career', usesWordCount: false, baseDays: 3 },
    sop: { baseMultiplier: 0.7, minDays: 4, maxDays: 10, label: 'Statement of Purpose', category: 'career', usesWordCount: true },
    
    // ========================================
    // CAREER SERVICES - Digital (Complex, longer turnaround)
    // ========================================
    web_resume: { 
      baseMultiplier: 1.5, 
      minDays: 7, 
      maxDays: 14, 
      label: 'Web Resume/Portfolio', 
      category: 'career-digital',
      usesWordCount: false, 
      baseDays: 10,
      complexityTiers: { basic: 7, standard: 10, premium: 14 }
    },
    personal_website: { 
      baseMultiplier: 2.0, 
      minDays: 10, 
      maxDays: 21, 
      label: 'Personal Website', 
      category: 'career-digital',
      usesWordCount: false, 
      baseDays: 14,
      complexityTiers: { basic: 10, standard: 14, premium: 21 }
    },
    portfolio_website: { 
      baseMultiplier: 1.8, 
      minDays: 10, 
      maxDays: 18, 
      label: 'Portfolio Website', 
      category: 'career-digital',
      usesWordCount: false, 
      baseDays: 12,
      complexityTiers: { basic: 10, standard: 12, premium: 18 }
    },
    
    // ========================================
    // CAREER SERVICES - Coaching (Session-based)
    // ========================================
    interview_prep: { 
      baseMultiplier: 0.5, 
      minDays: 2, 
      maxDays: 7, 
      label: 'Interview Preparation', 
      category: 'career-coaching',
      usesWordCount: false, 
      baseDays: 3,
      sessionBased: true
    },
    job_search: { 
      baseMultiplier: 0.8, 
      minDays: 5, 
      maxDays: 14, 
      label: 'Job Search Strategy', 
      category: 'career-coaching',
      usesWordCount: false, 
      baseDays: 7
    },
    salary_negotiation: { 
      baseMultiplier: 0.4, 
      minDays: 2, 
      maxDays: 5, 
      label: 'Salary Negotiation', 
      category: 'career-coaching',
      usesWordCount: false, 
      baseDays: 3,
      sessionBased: true
    },
    networking_email: { 
      baseMultiplier: 0.3, 
      minDays: 1, 
      maxDays: 3, 
      label: 'Networking Emails', 
      category: 'career',
      usesWordCount: false, 
      baseDays: 2
    },
    industry_transition: { 
      baseMultiplier: 1.0, 
      minDays: 7, 
      maxDays: 14, 
      label: 'Industry Transition Package', 
      category: 'career-coaching',
      usesWordCount: false, 
      baseDays: 10
    },
    personal_branding: { 
      baseMultiplier: 1.2, 
      minDays: 7, 
      maxDays: 14, 
      label: 'Personal Branding', 
      category: 'career',
      usesWordCount: false, 
      baseDays: 10
    },
    
    // ========================================
    // BUSINESS SERVICES
    // ========================================
    business_plan: { baseMultiplier: 1.4, minDays: 7, label: 'Business Plan', category: 'business' },
    market_research: { baseMultiplier: 1.35, minDays: 5, label: 'Market Research', category: 'business' },
    financial_analysis: { baseMultiplier: 1.4, minDays: 5, label: 'Financial Analysis', category: 'business' },
    
    // ========================================
    // DESIGN SERVICES
    // ========================================
    logo_design: {
      baseMultiplier: 1.0,
      minDays: 3,
      maxDays: 10,
      label: 'Logo Design',
      category: 'design',
      usesWordCount: false,
      baseDays: 5,
      complexityTiers: { basic: 3, standard: 5, complex: 8, premium: 10 }
    },
    brand_identity: {
      baseMultiplier: 1.5,
      minDays: 7,
      maxDays: 21,
      label: 'Brand Identity Package',
      category: 'design',
      usesWordCount: false,
      baseDays: 12,
      complexityTiers: { basic: 7, standard: 12, complex: 16, premium: 21 }
    },
    graphic_design: {
      baseMultiplier: 0.8,
      minDays: 2,
      maxDays: 7,
      label: 'Graphic Design',
      category: 'design',
      usesWordCount: false,
      baseDays: 3,
      // Pages/images affect delivery time
      pagesMultiplier: { '1-3': 1.0, '4-10': 1.5, '11-20': 2.0, '21+': 2.5 }
    },
    presentation_design: {
      baseMultiplier: 0.6,
      minDays: 2,
      maxDays: 10,
      label: 'Presentation Design',
      category: 'design',
      usesWordCount: false,
      baseDays: 3,
      slidesMultiplier: { '1-10': 1.0, '11-25': 1.4, '26-50': 1.8, '51+': 2.2 }
    },
    social_media_kit: {
      baseMultiplier: 0.9,
      minDays: 3,
      maxDays: 10,
      label: 'Social Media Kit',
      category: 'design',
      usesWordCount: false,
      baseDays: 5,
      complexityTiers: { basic: 3, standard: 5, complex: 7, premium: 10 }
    },
    illustration: {
      baseMultiplier: 1.2,
      minDays: 3,
      maxDays: 14,
      label: 'Custom Illustration',
      category: 'design',
      usesWordCount: false,
      baseDays: 5,
      complexityTiers: { basic: 3, standard: 5, complex: 10, premium: 14 }
    },
    packaging_design: {
      baseMultiplier: 1.3,
      minDays: 5,
      maxDays: 14,
      label: 'Packaging Design',
      category: 'design',
      usesWordCount: false,
      baseDays: 7,
      complexityTiers: { basic: 5, standard: 7, complex: 10, premium: 14 }
    },
    print_design: {
      baseMultiplier: 1.0,
      minDays: 3,
      maxDays: 12,
      label: 'Print Design',
      category: 'design',
      usesWordCount: false,
      baseDays: 5,
      pagesMultiplier: { '1-4': 1.0, '5-12': 1.5, '13-30': 2.0, '31+': 2.5 }
    },
    motion_graphics: {
      baseMultiplier: 1.8,
      minDays: 5,
      maxDays: 21,
      label: 'Motion Graphics',
      category: 'design',
      usesWordCount: false,
      baseDays: 7,
      durationMultiplier: { '0-15s': 1.0, '16-30s': 1.5, '31-60s': 2.0, '60+s': 2.5 }
    },
    infographic: {
      baseMultiplier: 1.0,
      minDays: 3,
      maxDays: 10,
      label: 'Infographic Design',
      category: 'design',
      usesWordCount: false,
      baseDays: 4,
      complexityTiers: { basic: 3, standard: 4, complex: 7, premium: 10 }
    },
    
    // ========================================
    // DIGITAL SERVICES
    // ========================================
    website_design: {
      baseMultiplier: 2.0,
      minDays: 10,
      maxDays: 45,
      label: 'Website Design',
      category: 'digital',
      usesWordCount: false,
      baseDays: 14,
      pagesMultiplier: { '1-5': 1.0, '6-10': 1.5, '11-20': 2.2, '21+': 3.0 },
      complexityTiers: { landing: 10, standard: 14, ecommerce: 25, custom: 45 }
    },
    web_development: {
      baseMultiplier: 2.5,
      minDays: 14,
      maxDays: 60,
      label: 'Web Development',
      category: 'digital',
      usesWordCount: false,
      baseDays: 21,
      complexityTiers: { static: 14, dynamic: 21, webapp: 35, enterprise: 60 }
    },
    mobile_app_design: {
      baseMultiplier: 2.2,
      minDays: 14,
      maxDays: 45,
      label: 'Mobile App Design',
      category: 'digital',
      usesWordCount: false,
      baseDays: 21,
      screensMultiplier: { '1-10': 1.0, '11-25': 1.5, '26-50': 2.0, '51+': 2.5 }
    },
    ui_ux_design: {
      baseMultiplier: 1.8,
      minDays: 7,
      maxDays: 30,
      label: 'UI/UX Design',
      category: 'digital',
      usesWordCount: false,
      baseDays: 14,
      complexityTiers: { basic: 7, standard: 14, advanced: 21, enterprise: 30 }
    },
    ecommerce_setup: {
      baseMultiplier: 2.0,
      minDays: 14,
      maxDays: 35,
      label: 'E-Commerce Setup',
      category: 'digital',
      usesWordCount: false,
      baseDays: 21,
      productsMultiplier: { '1-25': 1.0, '26-100': 1.3, '101-500': 1.6, '500+': 2.0 }
    },
    seo_optimization: {
      baseMultiplier: 1.2,
      minDays: 7,
      maxDays: 30,
      label: 'SEO Optimization',
      category: 'digital',
      usesWordCount: false,
      baseDays: 14,
      complexityTiers: { audit: 7, basic: 14, advanced: 21, enterprise: 30 }
    },
    social_media_management: {
      baseMultiplier: 1.0,
      minDays: 3,
      maxDays: 14,
      label: 'Social Media Management',
      category: 'digital',
      usesWordCount: false,
      baseDays: 7,
      // Ongoing service - delivery is for initial setup
      complexityTiers: { basic: 3, standard: 7, premium: 10, enterprise: 14 }
    },
    content_management: {
      baseMultiplier: 1.3,
      minDays: 5,
      maxDays: 21,
      label: 'CMS Setup & Migration',
      category: 'digital',
      usesWordCount: false,
      baseDays: 10,
      complexityTiers: { basic: 5, standard: 10, advanced: 14, enterprise: 21 }
    },
    email_marketing: {
      baseMultiplier: 0.8,
      minDays: 3,
      maxDays: 14,
      label: 'Email Marketing',
      category: 'digital',
      usesWordCount: false,
      baseDays: 5,
      complexityTiers: { template: 3, campaign: 5, automation: 10, enterprise: 14 }
    },
    video_production: {
      baseMultiplier: 2.0,
      minDays: 7,
      maxDays: 30,
      label: 'Video Production',
      category: 'digital',
      usesWordCount: false,
      baseDays: 10,
      durationMultiplier: { '0-60s': 1.0, '1-3min': 1.5, '3-10min': 2.0, '10+min': 3.0 }
    },
    animation: {
      baseMultiplier: 2.2,
      minDays: 7,
      maxDays: 35,
      label: 'Animation',
      category: 'digital',
      usesWordCount: false,
      baseDays: 14,
      durationMultiplier: { '0-30s': 1.0, '31-60s': 1.5, '1-2min': 2.0, '2+min': 2.5 }
    }
  };
  
  // Career service complexity levels (affects delivery time)
  // Maps to career-services.js complexity: basic, standard, senior, specialized
  const CAREER_COMPLEXITY = {
    basic: { multiplier: 0.9, label: 'Entry Level', description: 'Early career / Fresh graduate' },
    standard: { multiplier: 1.0, label: 'Mid-Level', description: 'Standard professional experience' },
    senior: { multiplier: 1.3, label: 'Senior/Executive', description: 'Leadership & executive positions' },
    specialized: { multiplier: 1.5, label: 'Specialized', description: 'Industry-specific or technical' },
    // Legacy mappings for backwards compatibility
    premium: { multiplier: 1.3, label: 'Premium', description: 'Comprehensive package' },
    enterprise: { multiplier: 1.5, label: 'Enterprise', description: 'Custom solutions' }
  };
  
  // Design service complexity levels
  const DESIGN_COMPLEXITY = {
    basic: { multiplier: 0.7, label: 'Basic', description: 'Simple, single-concept design' },
    standard: { multiplier: 1.0, label: 'Standard', description: 'Multi-concept with revisions' },
    complex: { multiplier: 1.5, label: 'Complex', description: 'Detailed, multi-element design' },
    premium: { multiplier: 2.0, label: 'Premium', description: 'Brand-level comprehensive design' }
  };
  
  // Design pages/images quantity multiplier
  const DESIGN_QUANTITY_MULTIPLIER = {
    1: 1.0,
    2: 1.4,
    3: 1.7,
    4: 2.0,
    5: 2.2,
    '6-10': 2.8,
    '11-20': 3.5,
    '21+': 4.5
  };
  
  // Digital service complexity levels
  const DIGITAL_COMPLEXITY = {
    basic: { multiplier: 0.6, label: 'Basic', description: 'Simple static implementation' },
    standard: { multiplier: 1.0, label: 'Standard', description: 'Standard dynamic features' },
    advanced: { multiplier: 1.6, label: 'Advanced', description: 'Complex interactive features' },
    enterprise: { multiplier: 2.2, label: 'Enterprise', description: 'Full-scale enterprise solution' }
  };
  
  // Digital pages/screens quantity multiplier
  const DIGITAL_PAGES_MULTIPLIER = {
    1: 1.0,
    2: 1.2,
    3: 1.4,
    4: 1.6,
    5: 1.8,
    '6-10': 2.2,
    '11-20': 3.0,
    '21+': 4.0
  };
  
  // External requirements additional days
  const EXTERNAL_REQUIREMENTS = {
    none: 0,
    software_analysis: 2,    // SPSS, STATA, NVivo, etc.
    coding_required: 3,      // Programming/development
    lab_access: 5,           // Laboratory requirements
    field_research: 7,       // Field work, travel
    equipment_needed: 4,     // Special equipment
    ethics_approval: 5,      // Ethics committee approval wait time
    multi_site: 6            // Multiple locations involved
  };
  
  // Number of chapters impact for dissertations/theses
  const CHAPTER_MULTIPLIERS = {
    1: 1.0,
    2: 1.7,
    3: 2.3,
    4: 2.9,
    5: 3.5,
    6: 4.0
  };
  
  // Rush delivery tier configurations (percentages relative to standard)
  // These are calculated dynamically based on standard delivery time
  const RUSH_TIER_CONFIG = {
    standard: {
      label: 'Standard',
      multiplier: 1.0,
      priceMultiplier: 1.0,
      icon: '🕐',
      minDaysFromStandard: 0,  // Full standard time
      available: true
    },
    express: {
      label: 'Express',
      multiplier: 0.65,        // 65% of standard time
      priceMultiplier: 1.10,   // +10%
      icon: '⚡',
      minDays: 2,              // Never less than 2 days
      available: true
    },
    rush: {
      label: 'Rush',
      multiplier: 0.40,        // 40% of standard time  
      priceMultiplier: 1.15,   // +15%
      icon: '🔥',
      minDays: 1,              // Never less than 1 day
      available: true
    },
    emergency: {
      label: 'Emergency',
      multiplier: 0.25,        // 25% of standard time
      priceMultiplier: 1.25,   // +25% (increased from 20%)
      icon: '🆘',
      minDays: 0.5,            // 12 hours minimum
      maxStandardDays: 10,     // Only available if standard is <= 10 days
      available: true
    }
  };
  
  // ============================================================================
  // MAIN CALCULATOR CLASS
  // ============================================================================
  
  class DeliveryCalculator {
    constructor() {
      this.lastCalculation = null;
    }
    
    /* *
     * Calculate delivery time based on all factors
     * @param {Object} params - Calculation parameters
     * @returns {Object} Delivery calculation result with all tiers */
    calculate(params) {
      const {
        wordCount = 0,
        pages = 0,
        academicLevel = 'undergraduate',
        serviceType = 'essay',
        researchType = 'secondary',
        complexity = 'moderate',
        externalRequirements = [],
        chapters = 1,
        careerComplexity = 'standard', // For career services: basic, standard, premium, enterprise
        designComplexity = 'standard', // For design services: basic, standard, complex, premium
        digitalComplexity = 'standard', // For digital services: basic, standard, advanced, enterprise
        quantity = 1, // Number of pages/images/screens
        customFactors = {}
      } = params;
      
      // Get service configuration
      const service = SERVICE_ADJUSTMENTS[serviceType] || SERVICE_ADJUSTMENTS.essay;
      
      let baseDays;
      let effectiveWords = wordCount || (pages * 250);
      
      // ============================================
      // DESIGN SERVICES - Visual/Creative calculation
      // ============================================
      if (service.category === 'design') {
        // Use fixed base days with complexity adjustment
        baseDays = service.baseDays || service.minDays || 5;
        
        // Apply complexity tier if defined
        if (service.complexityTiers && service.complexityTiers[designComplexity]) {
          baseDays = service.complexityTiers[designComplexity];
        } else {
          // Apply design complexity multiplier
          const designComplexityConfig = DESIGN_COMPLEXITY[designComplexity] || DESIGN_COMPLEXITY.standard;
          baseDays *= designComplexityConfig.multiplier;
        }
        
        // Apply quantity multiplier (pages/images)
        if (quantity > 1) {
          const quantityMultiplier = this._getQuantityMultiplier(quantity, DESIGN_QUANTITY_MULTIPLIER);
          baseDays *= quantityMultiplier;
        }
        
        // Apply minimum and maximum for design services
        const minDays = service.minDays || 2;
        const maxDays = service.maxDays || 21;
        baseDays = Math.max(minDays, Math.min(maxDays, baseDays));
        
      }
      // ============================================
      // DIGITAL SERVICES - Tech/Development calculation
      // ============================================
      else if (service.category === 'digital') {
        // Use fixed base days with complexity adjustment
        baseDays = service.baseDays || service.minDays || 14;
        
        // Apply complexity tier if defined
        if (service.complexityTiers && service.complexityTiers[digitalComplexity]) {
          baseDays = service.complexityTiers[digitalComplexity];
        } else {
          // Apply digital complexity multiplier
          const digitalComplexityConfig = DIGITAL_COMPLEXITY[digitalComplexity] || DIGITAL_COMPLEXITY.standard;
          baseDays *= digitalComplexityConfig.multiplier;
        }
        
        // Apply pages/screens multiplier
        if (quantity > 1) {
          const pagesMultiplier = this._getQuantityMultiplier(quantity, DIGITAL_PAGES_MULTIPLIER);
          baseDays *= pagesMultiplier;
        }
        
        // Apply minimum and maximum for digital services
        const minDays = service.minDays || 7;
        const maxDays = service.maxDays || 60;
        baseDays = Math.max(minDays, Math.min(maxDays, baseDays));
        
      }
      // ============================================
      // CAREER SERVICES - Different calculation logic
      // ============================================
      else if (service.category && service.category.startsWith('career')) {
        // Career services don't always use word count
        if (service.usesWordCount === false) {
          // Use fixed base days with complexity adjustment
          baseDays = service.baseDays || service.minDays || 3;
          
          // Apply complexity tier for digital services (websites, portfolios)
          if (service.complexityTiers && service.complexityTiers[careerComplexity]) {
            baseDays = service.complexityTiers[careerComplexity];
          } else {
            // Apply career complexity multiplier
            const careerComplexityConfig = CAREER_COMPLEXITY[careerComplexity] || CAREER_COMPLEXITY.standard;
            baseDays *= careerComplexityConfig.multiplier;
          }
          
          // For session-based services, consider number of sessions
          if (service.sessionBased && customFactors.sessions) {
            baseDays += (customFactors.sessions - 1) * 1; // +1 day per additional session
          }
          
        } else {
          // Career services that do use word count (personal statements, SOPs)
          const basePerThousand = BASE_DAYS_PER_1000_WORDS.professional;
          baseDays = (effectiveWords / 1000) * basePerThousand;
          baseDays *= service.baseMultiplier;
        }
        
        // Apply minimum and maximum for career services
        const minDays = service.minDays || 1;
        const maxDays = service.maxDays || 21;
        baseDays = Math.max(minDays, Math.min(maxDays, baseDays));
        
      } else {
        // ============================================
        // ACADEMIC SERVICES - Original calculation logic
        // ============================================
        
        // Step 1: Calculate base days from word count
        const basePerThousand = BASE_DAYS_PER_1000_WORDS[academicLevel] || BASE_DAYS_PER_1000_WORDS.undergraduate;
        baseDays = (effectiveWords / 1000) * basePerThousand;
        
        // Step 2: Apply service type adjustment
        baseDays *= service.baseMultiplier;
        
        // Step 3: Apply research type multiplier
        const researchMultiplier = RESEARCH_TYPE_MULTIPLIERS[researchType] || 1.0;
        baseDays *= researchMultiplier;
        
        // Step 4: Apply complexity multiplier
        const complexityMultiplier = COMPLEXITY_MULTIPLIERS[complexity] || 1.0;
        baseDays *= complexityMultiplier;
        
        // Step 5: Apply chapter multiplier for dissertations/theses
        if (['dissertation_chapter', 'full_dissertation', 'thesis'].includes(serviceType) && chapters > 1) {
          const chapterMultiplier = CHAPTER_MULTIPLIERS[Math.min(chapters, 6)] || CHAPTER_MULTIPLIERS[6];
          baseDays *= chapterMultiplier;
        }
        
        // Step 6: Add external requirements days
        let externalDays = 0;
        if (Array.isArray(externalRequirements)) {
          externalRequirements.forEach(req => {
            externalDays += EXTERNAL_REQUIREMENTS[req] || 0;
          });
        }
        baseDays += externalDays;
        
        // Step 7: Apply minimum and maximum constraints for academic
        const minDays = Math.max(MINIMUM_DAYS[academicLevel] || 2, service.minDays || 1);
        const maxDays = MAXIMUM_DAYS[academicLevel] || 30;
        baseDays = Math.max(minDays, Math.min(maxDays, baseDays));
      }
      
      // Apply any custom factors (both academic and career)
      if (customFactors.additionalDays) {
        baseDays += customFactors.additionalDays;
      }
      if (customFactors.multiplier) {
        baseDays *= customFactors.multiplier;
      }
      
      let standardDays = Math.ceil(baseDays);
      
      // Step 9: Calculate all rush tiers dynamically
      const tiers = this._calculateRushTiers(standardDays, academicLevel, service);
      
      // Determine if this is a career service
      const isCareerService = service.category && service.category.startsWith('career');
      
      // Build result object
      const result = {
        standardDays,
        effectiveWords,
        academicLevel,
        serviceType,
        serviceName: service.label,
        serviceCategory: service.category || 'academic',
        isCareerService,
        researchType: isCareerService ? null : researchType,
        complexity: isCareerService ? careerComplexity : complexity,
        externalRequirements,
        chapters,
        breakdown: {
          baseDays: Math.round(baseDays * 10) / 10,
          serviceAdjustment: service.baseMultiplier,
          isFixedDelivery: service.usesWordCount === false,
          finalStandard: standardDays
        },
        tiers,
        recommendations: this._generateRecommendations(params, standardDays, service)
      };
      
      this.lastCalculation = result;
      return result;
    }
    
    /* *
     * Get quantity multiplier from lookup table
     * @param {number} quantity - The quantity value
     * @param {Object} multiplierTable - The lookup table
     * @returns {number} The multiplier */
    _getQuantityMultiplier(quantity, multiplierTable) {
      // Direct number lookup
      if (multiplierTable[quantity]) {
        return multiplierTable[quantity];
      }
      
      // Range lookup (e.g., '6-10', '11-20', '21+')
      if (quantity >= 21 && multiplierTable['21+']) {
        return multiplierTable['21+'];
      }
      if (quantity >= 11 && quantity <= 20 && multiplierTable['11-20']) {
        return multiplierTable['11-20'];
      }
      if (quantity >= 6 && quantity <= 10 && multiplierTable['6-10']) {
        return multiplierTable['6-10'];
      }
      
      // Default to 1.0 if not found
      return 1.0;
    }
    
    /* *
     * Calculate rush tier times dynamically based on standard delivery */
    _calculateRushTiers(standardDays, academicLevel, service) {
      const tiers = {};
      
      for (const [tierKey, tierConfig] of Object.entries(RUSH_TIER_CONFIG)) {
        const tier = { ...tierConfig };
        
        if (tierKey === 'standard') {
          tier.days = standardDays;
          tier.displayTime = this._formatDays(standardDays);
          tier.available = true;
        } else {
          // Calculate rush tier days
          let rushDays = standardDays * tier.multiplier;
          
          // Apply minimum days constraint
          rushDays = Math.max(tier.minDays || 0.5, rushDays);
          
          // Round appropriately
          if (rushDays < 1) {
            rushDays = Math.ceil(rushDays * 24) / 24; // Round to hours
          } else {
            rushDays = Math.ceil(rushDays);
          }
          
          tier.days = rushDays;
          tier.displayTime = this._formatDays(rushDays);
          
          // Check availability constraints
          tier.available = true;
          
          // Emergency not available for very long projects
          if (tierKey === 'emergency' && tier.maxStandardDays && standardDays > tier.maxStandardDays) {
            tier.available = false;
            tier.unavailableReason = 'Project scope too large for emergency delivery';
          }
          
          // Check if rush would be same as or longer than slower tier
          // (e.g., if express ends up being same as standard, disable it)
          if (rushDays >= standardDays) {
            tier.available = false;
            tier.unavailableReason = 'Not faster than standard delivery';
          }
        }
        
        tiers[tierKey] = tier;
      }
      
      // Adjust pricing if rush tiers are very tight
      if (tiers.express.days && tiers.express.days <= 2) {
        tiers.express.priceMultiplier = 1.15; // Increase price for very fast
      }
      if (tiers.rush.days && tiers.rush.days <= 1) {
        tiers.rush.priceMultiplier = 1.20;
      }
      
      return tiers;
    }
    
    /* *
     * Format days into human-readable time */
    _formatDays(days) {
      if (days < 0.5) {
        return `${Math.ceil(days * 24)} hours`;
      } else if (days < 1) {
        const hours = Math.ceil(days * 24);
        return `${hours} hours`;
      } else if (days === 1) {
        return '24 hours';
      } else if (days <= 2) {
        return `${days} days (${Math.ceil(days * 24)} hours)`;
      } else if (days <= 7) {
        return `${Math.ceil(days)} days`;
      } else if (days <= 14) {
        const weeks = Math.floor(days / 7);
        const remainingDays = Math.ceil(days % 7);
        if (remainingDays === 0) {
          return `${weeks} week${weeks > 1 ? 's' : ''}`;
        }
        return `${weeks} week${weeks > 1 ? 's' : ''}, ${remainingDays} days`;
      } else {
        const weeks = Math.ceil(days / 7);
        return `${weeks} weeks (${Math.ceil(days)} days)`;
      }
    }
    
    /* *
     * Generate recommendations based on the calculation */
    _generateRecommendations(params, standardDays, service = {}) {
      const recommendations = [];
      const isCareer = service.category && service.category.startsWith('career');
      
      // Career service recommendations
      if (isCareer) {
        if (service.category === 'career-digital') {
          recommendations.push({
            type: 'info',
            message: 'Digital deliverables include design mockups for approval before final delivery'
          });
        }
        
        if (params.careerComplexity === 'premium' || params.careerComplexity === 'enterprise') {
          recommendations.push({
            type: 'info',
            message: 'Premium packages include additional revision rounds and priority support'
          });
        }
        
        if (['personal_website', 'web_resume', 'portfolio_website'].includes(params.serviceType)) {
          recommendations.push({
            type: 'warning',
            message: 'Website projects require your content/materials to be submitted within 48 hours of order'
          });
        }
        
        return recommendations;
      }
      
      // Academic service recommendations
      // Recommend for complex projects
      if (params.complexity === 'expert' || params.complexity === 'specialized') {
        recommendations.push({
          type: 'info',
          message: 'Complex projects benefit from standard delivery to ensure quality'
        });
      }
      
      // Recommend for primary research
      if (params.researchType && params.researchType.startsWith('primary')) {
        recommendations.push({
          type: 'warning',
          message: 'Primary research may require additional time for data collection'
        });
      }
      
      // Long project warning
      if (standardDays > 21) {
        recommendations.push({
          type: 'info',
          message: 'Large projects are delivered in phases for quality assurance'
        });
      }
      
      // PhD level advice
      if (params.academicLevel === 'phd') {
        recommendations.push({
          type: 'info',
          message: 'PhD-level work includes rigorous methodology and analysis'
        });
      }
      
      return recommendations;
    }
    
    /* *
     * Get quick estimate for UI display */
    getQuickEstimate(wordCount, academicLevel = 'undergraduate', serviceType = 'essay') {
      return this.calculate({
        wordCount,
        academicLevel,
        serviceType,
        researchType: 'secondary',
        complexity: 'moderate'
      });
    }
    
    /* *
     * Get available rush options for given standard time */
    getAvailableRushOptions(standardDays) {
      const tiers = this._calculateRushTiers(standardDays, 'undergraduate', SERVICE_ADJUSTMENTS.essay);
      return Object.entries(tiers)
        .filter(([_, tier]) => tier.available)
        .map(([key, tier]) => ({
          key,
          ...tier
        }));
    }
    
    /* *
     * Format the tier for display in UI */
    formatTierForDisplay(tier) {
      return {
        label: tier.label,
        time: tier.displayTime,
        price: tier.priceMultiplier === 1.0 
          ? 'Included' 
          : `+${Math.round((tier.priceMultiplier - 1) * 100)}%`,
        icon: tier.icon,
        available: tier.available,
        unavailableReason: tier.unavailableReason
      };
    }
  }
  
// ============================================================================
// EXPORTS
// ============================================================================

// Create singleton instance
const calculator = new DeliveryCalculator();

// Backward compatibility - expose to global scope
window.DeliveryCalculator = DeliveryCalculator;
window.deliveryCalculator = calculator;

// Also expose configuration for reference
const DELIVERY_CONFIG = {
  academicLevels: Object.keys(BASE_DAYS_PER_1000_WORDS),
  serviceTypes: Object.keys(SERVICE_ADJUSTMENTS),
  researchTypes: Object.keys(RESEARCH_TYPE_MULTIPLIERS),
  complexityLevels: Object.keys(COMPLEXITY_MULTIPLIERS),
  externalRequirements: Object.keys(EXTERNAL_REQUIREMENTS),
  rushTiers: Object.keys(RUSH_TIER_CONFIG)
};
window.DELIVERY_CONFIG = DELIVERY_CONFIG;

// ES Module exports
export default calculator;
export { DeliveryCalculator, calculator, DELIVERY_CONFIG };
